<script setup lang="ts">
import { getShippingMethodDeliveryTime } from "@shopware/helpers";
import { onMounted, watch } from "vue";

const emits = defineEmits(["shipping-method-changed", "close"]);

const { shippingMethods, getShippingMethods } = useCheckout();
const { refreshCart } = useCart();

const isLoading = ref(false);

const { selectedShippingMethod: shippingMethod, setShippingMethod } =
  useSessionContext();

const pendingShippingMethodId = ref<string>("");

onMounted(async () => {
  isLoading.value = true;
  await getShippingMethods();
  if (shippingMethod.value) {
    pendingShippingMethodId.value = shippingMethod.value.id;
  }
  isLoading.value = false;
});

// Keep local selection in sync if shipping method changes elsewhere
watch(shippingMethod, (newVal) => {
  if (newVal?.id) pendingShippingMethodId.value = newVal.id;
});

const isSubmitLoading = ref(false);
const confirmChangeShippingMethod = async () => {
  if (
    pendingShippingMethodId.value &&
    pendingShippingMethodId.value !== shippingMethod.value?.id
  ) {
    isSubmitLoading.value = true;
    try {
      await setShippingMethod({ id: pendingShippingMethodId.value });
      // Refresh cart to update shipping costs and rule-based pseudo products
      await refreshCart();
      emits("shipping-method-changed", pendingShippingMethodId.value);
    } finally {
      isSubmitLoading.value = false;
    }
  }
};
</script>

<template>
  <div class="mb-10">
    <h3
      class="font-medium text-grey-700 text-lg 3xl:text-xl leading-tight line-clamp-1"
    >
      {{ $t("checkout.shippingMethodLabel") }}
    </h3>
    <div class="mt-5 flex flex-col">
      <div v-if="isLoading || !shippingMethods.length" class="w-full">
        <div class="flex animate-pulse flex-col space-y-4">
          <div class="flex items-center space-x-4 p-4 rounded-md">
            <div class="w-4 bg-light-grey h-4 rounded-full" />
            <div class="flex flex-col space-y-2 flex-1">
              <div class="w-48 bg-light-grey h-5 rounded-md" />
              <div class="w-32 bg-light-grey h-4 rounded-md" />
            </div>
          </div>
          <div class="flex items-center space-x-4 p-4 rounded-md">
            <div class="w-4 bg-light-grey h-4 rounded-full" />
            <div class="flex flex-col space-y-2 flex-1">
              <div class="w-40 bg-light-grey h-5 rounded-md" />
              <div class="w-28 bg-light-grey h-4 rounded-md" />
            </div>
          </div>
        </div>
      </div>
      <template v-else>
        <label
          v-for="singleShippingMethod in shippingMethods"
          :key="singleShippingMethod.id"
          class="flex items-start gap-3 cursor-pointer select-none mb-4"
          :class="
            singleShippingMethod.id === pendingShippingMethodId
              ? 'order-first'
              : 'order-0'
          "
        >
          <input
            :id="singleShippingMethod.id"
            v-model="pendingShippingMethodId"
            :value="singleShippingMethod.id"
            name="shipping-method"
            type="radio"
            class="mt-1"
            :disabled="isSubmitLoading"
            :data-testid="`checkout-shipping-method-${singleShippingMethod.id}`"
            @change="confirmChangeShippingMethod"
          />
          <div class="space-y-1">
            <div class="font-semibold">
              <span>{{ singleShippingMethod.translated.name }}</span>
              <span v-if="getShippingMethodDeliveryTime(singleShippingMethod)">
                (
                {{ getShippingMethodDeliveryTime(singleShippingMethod) }})</span
              >
              <SharedLandingPageLink
                title="Delivery Cost Overview"
                :open-in-dialog="true"
                class="text-primary font-semibold underline ml-1"
              >
                {{ $t("checkout.deliveryCostOverview") }}
              </SharedLandingPageLink>
            </div>
            
            <p
              class="text-sm text-grey-700 mt-1"
              v-if="singleShippingMethod.translated.description"
            >
              {{ singleShippingMethod.translated.description }}
            </p>
          </div>
        </label>
      </template>
    </div>
  </div>
</template>
